import { useState } from "react";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useAppSelector } from "@app/hooks";
import { BiSave } from "react-icons/bi";
import { ActivityUpdatePayload } from "@app/features/activities/activityTypes";
import TranslatableText from "@components/translations/TranslatableText";
import ConfirmationModal from "@components/modal/ConfirmationModal";
import EditActivityContent from "./EditActivityContent";
import ModuleButton from "@components/button/ModuleButton";
import * as Yup from "yup";
import EditHeaderButtons from './EditHeaderButtons';
import { Tab } from "./EditActivityCombined";

interface EditActivityProps {
  blockingError: string;
  id: number | null;
  initialvalues: ActivityUpdatePayload;
  validationSchema: Yup.ObjectSchema<any>;
  handleSubmit: (values: ActivityUpdatePayload) => void;
  isUpdateLoading: boolean;
  isFetching: boolean;
  updateErrorMessage: string;
  setShouldBlockTabs?: (shouldBlock: boolean) => void;
  handleTabChange: (tab: Tab) => void;
}

const EditActivity = ({ blockingError, id, initialvalues, validationSchema, handleSubmit, isUpdateLoading, isFetching, updateErrorMessage, setShouldBlockTabs, handleTabChange }: EditActivityProps) => {
  return blockingError ? (
    <section>
      <div className="module-group ms-3">
        <div className="pos-relative mb-2 pb-1 d-flex f-column justify-content-center gap-8">
          <h2 className="fs-14 ff-medium-ext module-header w-fit">
            <TranslatableText translationKey="main.edit_activity" />
          </h2>
          <EditHeaderButtons handleTabChange={handleTabChange} tab={Tab.Activity} />
        </div>

        <span className="error fs-13 pb-2">{blockingError}</span>
      </div>
    </section>
  ) : (
    <section>
      <div className="module-group ms-3">
        <div className="pos-relative mb-2 pb-1 d-flex f-column justify-content-center gap-8">
          <h2 className="fs-14 ff-medium-ext module-header w-fit">
            <TranslatableText translationKey="main.edit_activity" />
          </h2>
          <EditHeaderButtons handleTabChange={handleTabChange} tab={Tab.Activity} />
        </div>

        <div className="d-flex f-column" style={{ maxWidth: "550px" }}>
          <Formik
            initialValues={initialvalues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ touched, dirty }) => {
              if (setShouldBlockTabs) setShouldBlockTabs(dirty && Object.keys(touched).length > 0);

              return (
                <Form>
                  <EditActivityContent isLoading={isFetching || isUpdateLoading} />
                  <p
                    className={`text-error pt-2 fs-13 ${updateErrorMessage && updateErrorMessage.length > 0
                      ? "pb-2"
                      : null
                      }`}
                  >
                    {updateErrorMessage}
                  </p>
                  <div className="pb-2 d-flex align-items-center gap-8">
                    <ModuleButton
                      disabled={isUpdateLoading}
                      isLoading={isUpdateLoading}
                      icon={BiSave}
                      labelKey="main.save_changes"
                    />
                  </div>
                </Form>

              )
            }}
          </Formik>
        </div>
      </div>
      {/* {
        isUpdateSuccess && <EditActivitySuccessModal />
      } */}
    </section>
  )
};

export default EditActivity;

const EditActivitySuccessModal = () => {
  const { project } = useAppSelector(selectCurrentProject);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(true);
  const navigate = useNavigate();

  const handleConfirmation = () => {
    setIsModalOpen(false);
    navigate(`/project/${project?.id ?? "new"}/activities`);
  };

  return (
    <ConfirmationModal
      onConfirm={handleConfirmation}
      open={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      message={<TranslatableText translationKey="main.edit_activity_success" />}
      confirmationMessage="main.back_to_activities"
      cancelButtonMessage="main.keep_editing"
    />
  )
}